import React, { useState, useRef, useEffect } from "react";
import ReactDOM from 'react-dom';
import { IoOpenOutline } from "react-icons/io5";
import { FaCaretLeft } from "react-icons/fa";
import { MdContentCopy } from "react-icons/md";
import { motion, AnimatePresence } from "framer-motion";
import axios from 'axios';
import parse from 'html-react-parser';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';


// CitationTooltip Component
const CitationTooltip = ({ isVisible, citation}) => {
  const [copySuccess, setCopySuccess] = useState(false);


  if (!isVisible) return null;
  if (!citation) {
    return (
      <motion.div 
        className="absolute left-[-115px] bottom-full transform -translate-x-1/2 mb-2 bg-red-50 border border-red-300 shadow-lg rounded-lg p-4 w-64 text-center z-10"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2 }}
      >
        <p className="text-sm text-red-600">An error occurred while fetching citation data. We apologize for the inconvenience.</p>
      </motion.div>
    );
  }

  const { title, year, authors, url, APA, paper_id } = citation;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(APA);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Create portal content
  const tooltipContent = (
    <motion.div 
      className="absolute bottom-[35px] left-[-120px] -translate-x-1/2 -translate-y-2 bg-white/95 backdrop-blur-sm border border-gray-300 shadow-lg rounded-lg p-4 w-64 text-center z-50"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
    >
      <motion.strong 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="block text-sm font-bold text-gray-800"
      >
        {title}
      </motion.strong>
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="mt-2 text-sm text-gray-600"
      >
        {year} - {authors}
      </motion.p>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex justify-center items-center mt-2 space-x-4"
      >
        <a 
          href={`https://sparkai.ederspark.com/paper/${paper_id}`} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="flex items-center text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 hover:scale-105"
        >
          <IoOpenOutline className="mr-1" />
          <span>View</span>
        </a>
        <button 
          onClick={handleCopy}
          className={`flex items-center text-sm transition-all duration-200 hover:scale-105 ${
            copySuccess 
              ? 'text-emerald-600' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <MdContentCopy className="mr-1" />
          <span>
            {copySuccess ? 'Copied!' : 'Copy'}
          </span>
        </button>
      </motion.div>
    </motion.div>
  );

  // Render directly in component instead of using portal
  return tooltipContent;
};

// CitationButton Component
const CitationButton = ({ number, citation}) => {
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 300); // 300ms delay before hiding tooltip
  };

  return (
    <span
      className="relative inline-block mx-1"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button className={citation 
        ? "flex items-center justify-center w-5 h-5 rounded-full bg-emerald-50 text-gray-700 border border-gray-600 text-xs font-bold drop-shadow-sm hover:bg-emerald-100 hover:scale-[1.15] transition-all duration-300"
        : "flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 border border-red-500 text-xs font-bold drop-shadow-sm cursor-not-allowed"
      }>
        {number}
      </button>
      <CitationTooltip 
        isVisible={isHovered} 
        citation={citation}
      />
    </span>
  );
};

const Formula = ({ formula, index }) => {
  return (
    <span
      className="formula bg-gray-100 p-2 rounded-2xl"
      key={index}
    >
      <InlineMath
        math={formula}
      />
    </span>
  )
}

export const CustomMarkdownRenderer = async ({ text }) => {

  const removeMarkdownTags = async (text) => {
    // Remove the ```markdown and ``` from the text
    let parsedText = text.replace(/```markdown/g, '');
    parsedText = parsedText.replace(/```/g, '');
    return parsedText;
  };

  const mergeCitations = async (text) => {
    // This regex looks for two or more consecutive "[number]" patterns
    // e.g., "[1][2]" or "[1][2][3]" etc.
    return text.replace(/(\[\d+\]){2,}/g, (match) => {
      // Extract all digits from the entire matched string (e.g. "1", "2", "3")
      const numbers = match.match(/\d+/g);
      // Return them combined in a single bracketed list, e.g. "[1,2,3]"
      return `[${numbers.join(',')}]`;
    });
  };
  

  const addFormulaTags = async (text) => {
    // Add the formula tags to the text
    // Will replace "\(" with "@formula(" and "\)" with "@/formula)"
    return text.replace(/\\\(/g, '@formula(').replace(/\\\)/g, ')@/formula');
  };

  const markdownToHtml = async (text) => {
    const url = `https://sparkai.ederspark.com/api/markdown/`;
    const parsedText = await removeMarkdownTags(text);
    const formulaText = await addFormulaTags(parsedText);
    const mergedCitations = await mergeCitations(formulaText);
    const response = await axios.post(url, { text: mergedCitations });
    console.log("response", response)
    return response.data.html;
  };

  return await markdownToHtml(text);
};

// MultipleCitationButton Component
const MultipleCitationButton = ({ numbers, citations}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const citationNumbers = numbers.split(',').map(n => n.trim());

  return (
    <span className="inline-flex items-center gap-1">
      {!isExpanded ? (
        <button 
          className="flex items-center justify-center px-2 h-5 rounded-full bg-emerald-50 text-gray-700 border border-gray-600 text-xs font-bold drop-shadow-sm hover:bg-emerald-100 hover:scale-[1.15] transition-all duration-300"
          onClick={() => setIsExpanded(true)}
        >
          {citationNumbers.join(',')}
        </button>
      ) : (
        <motion.span
          className="inline-flex items-center"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: "auto", opacity: 1 }}
          exit={{ width: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {citationNumbers.map((num, index) => (
            <React.Fragment key={num}>
              <CitationButton
                number={num}
                citation={citations[num]}
              />
            </React.Fragment>
          ))}
          <button 
            className="text-gray-400 hover:text-gray-600"
            onClick={() => setIsExpanded(false)}
          >
            <FaCaretLeft className="w-5 h-5" />
          </button>
        </motion.span>
      )}
    </span>
  );
};

// Update the RenderedHTML component
const RenderedHTML = ({ htmlContent, citations}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const responseRef = useRef(null);

  const processedHTML = parse(htmlContent, {
    replace: (node) => {
      if (node.type === 'text') {
        const parts = node.data.split(/(\[\d+(?:,\d+)*\]|@formula\(.*?\)@\/formula)/g);
        
        if (parts.length > 1) {
          return (
            <React.Fragment>
              {parts.map((part, index) => {
                const multipleCitationsMatch = part.match(/\[(\d+(?:,\d+)+)\]/);
                if (multipleCitationsMatch) {
                  return (
                    <MultipleCitationButton
                      key={index}
                      numbers={multipleCitationsMatch[1]}
                      citations={citations}
                    />
                  );
                }

                const citationMatch = part.match(/\[(\d+)\]/);
                if (citationMatch) {
                  return (
                    <CitationButton
                      key={index}
                      number={citationMatch[1]}
                      citation={citations[citationMatch[1]]}
                    />
                  );
                }

                const formulaMatch = part.match(/@formula\((.*?)\)@\/formula/);
                if (formulaMatch) {
                  return (
                    <Formula formula={formulaMatch[1]} index={index} />
                  );
                }

                return part;
              })}
            </React.Fragment>
          );
        }
      }
      return undefined;
    },
  });

  return (
    <div ref={responseRef} className="relative">
      {processedHTML}
    </div>
  );
};

const Citation = ({ citation, key, isExpanded}) => {
  const [ authorAPA, setAuthorAPA ] = React.useState(null);
  const [copySuccess, setCopySuccess] = React.useState(false);

  React.useEffect(() => {
    const author = citation.authors.split(',')[0];
    const authorAPA = `${author} et al.`;
    setAuthorAPA(authorAPA);
  }, [citation]);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(citation.APA);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className="flex flex-col w-full justify-start h-full">
      <span className="text-sm text-gray-600">
        {authorAPA} ({citation.year}) {citation.title}. {citation.journal}
      </span>
      {isExpanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="mt-3 text-sm border-t border-gray-200 pt-3"
        >
          <div className="flex flex-col gap-2">
            <div className="flex gap-4 mt-2 relative z-50">
              <a 
                href={`/paper/${citation.paper_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-sm text-gray-600 hover:text-gray-800 transition-all duration-200 hover:scale-105"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.open(`/paper/${citation.paper_id}`, '_blank');
                }}
              >
                <IoOpenOutline className="mr-1" />
                <span>View Paper</span>
              </a>
              <button
                onClick={handleCopy}
                className={`flex items-center text-sm transition-all duration-200 hover:scale-105 ${
                  copySuccess 
                    ? 'text-emerald-600' 
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <MdContentCopy className="mr-1" />
                <span>{copySuccess ? 'Copied!' : 'Copy Citation'}</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

const CitationList = ({ citations, response}) => {
  const [expandedCitation, setExpandedCitation] = React.useState(null);
  const [filterMode, setFilterMode] = React.useState('cited');

  const getUsedCitations = React.useMemo(() => {
    const usedCitations = {};
    Object.keys(citations || {}).forEach(key => {
      if (response.includes(`[${key}]`)) {
        usedCitations[key] = citations[key];
      }
    });
    return usedCitations;
  }, [citations, response]);

  const handleCitationClick = (key) => {
    setExpandedCitation(expandedCitation === key ? null : key);
  };

  const displayedCitations = filterMode === 'cited' ? getUsedCitations : citations;
  const citationCount = Object.keys(displayedCitations).length;

  return (
    <div className="mt-8 flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl mt-5 font-semibold text-gray-800">Citations</h2>
          <div className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium border border-emerald-200">
            {citationCount} {citationCount === 1 ? 'source' : 'sources'}
          </div>
        </div>
        
        <div className="flex p-1 bg-gradient-to-r from-emerald-100 to-green-100 rounded-xl shadow-sm border border-emerald-200">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setFilterMode('cited')}
            className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              filterMode === 'cited'
                ? 'text-emerald-800 bg-white shadow-sm'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            Cited Only
            {filterMode === 'cited' && (
              <motion.div
                layoutId="citationToggle"
                className="absolute inset-0 bg-white rounded-lg -z-10"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setFilterMode('all')}
            className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
              filterMode === 'all'
                ? 'text-emerald-800 bg-white shadow-sm'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            All Sources
            {filterMode === 'all' && (
              <motion.div
                layoutId="citationToggle"
                className="absolute inset-0 bg-white rounded-lg -z-10"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
          </motion.button>
        </div>
      </div>

      <motion.div 
        layout
        className="grid gap-3"
      >
        <AnimatePresence mode="popLayout">
          {Object.entries(displayedCitations || {}).map(([key, citation]) => (
            <motion.div
              key={key}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              onClick={() => handleCitationClick(key)}
              className={`group relative overflow-hidden ${
                expandedCitation === key 
                  ? 'bg-gradient-to-r from-emerald-100 to-green-100 border-emerald-200' 
                  : 'bg-white hover:bg-gray-50'
              } p-4 rounded-xl border border-gray-200 shadow-sm transition-all duration-300`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <CitationButton number={key} citation={citation} />
                </div>
                <Citation 
                  citation={citation} 
                  key={key} 
                  isExpanded={expandedCitation === key}
                />
                />
                <motion.div
                  animate={{ rotate: expandedCitation === key ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className={`ml-auto p-1 rounded-full ${
                    expandedCitation === key 
                      ? 'bg-emerald-100 text-emerald-600' 
                      : 'bg-gray-100 text-gray-400 group-hover:text-gray-600'
                  } transition-colors duration-200`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path d="M19 9l-7 7-7-7"></path>
                  </svg>
                </motion.div>
              </div>

              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/0 to-green-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

// ResponseRenderer Component
const ResponseRenderer = ({ 
  response, 
  citations, 
  textSize = "text-[0.95rem]", 
  textColor = "gray-900", 
  showCitations = true,
  allowActions = true
}) => {
  const [parsedContent, setParsedContent] = React.useState(null);

  React.useEffect(() => {
    console.log("Citations", citations)
    const parseResponse = async () => {
      try {
        const parsedMarkdown = await CustomMarkdownRenderer({ text: response });
        setParsedContent(parsedMarkdown);
      } catch (error) {
        console.error('Error parsing response:', error);
        setParsedContent(null);
      }
    };
    parseResponse()
  }, [response, citations]);

  return (
    <div className={`leading-[2.7rem] w-full max-w-[800px] relative ${textSize} text-${textColor}`}>
      <div className="markdown-body py-6">
        {
          parsedContent ? (
            <RenderedHTML 
              htmlContent={parsedContent} 
              citations={citations} 
              allowActions={allowActions}
            />
          ) : <div>Loading...</div>
        }
        {
          showCitations && (
            <CitationList citations={citations} response={response} />
          )
        }
      </div>
      <style jsx>{`
        .markdown-body {
          font-family: 'Noto Sans', sans-serif;
        }
        .markdown-body h1, .markdown-body h2, .markdown-body h3, .markdown-body h4 {
          display: block;
          font-weight: 600;
          margin-bottom: 1em;
        }
        .markdown-body h1 { font-size: 2em; }
        .markdown-body h2 { font-size: 1.5em; }
        .markdown-body h3 { font-size: 1.25em; }
        .markdown-body h4 { font-size: 1em; }
        .markdown-body ul, .markdown-body ol {
          display: block;
          padding-left: 2em;
          margin-bottom: 1em;
        }
        .markdown-body ul { list-style-type: disc; }
        .markdown-body ol { list-style-type: decimal; }
        .markdown-body li {
          display: list-item;
          margin-bottom: 0.5em;
        }
        .markdown-body p {
          display: inline;
          margin: 0;
        }
        .markdown-body br {
          display: block;
          margin: 0.5em 0;
          content: "";
        }
        .markdown-body hr {
          margin: 2em 0;
        }
      `}</style>
    </div>
  );
};

// Example usage
export const Test = () => {
  const response = "## Advancements in AI for Wetland Restoration and Biodiversity Enhancement\n\nBased on information retrieved from my database, several advancements are improving wetland restoration and biodiversity enhancement efforts using Artificial Intelligence (AI) [105].  These advancements span several key areas:\n\n### 1. Predictive Modeling and Site Selection\n\nAI-driven predictive models are enhancing the accuracy of wetland restoration outcomes [1].  These models integrate historical data with real-time environmental variables to improve prediction.  Machine learning algorithms are also being used to analyze habitat suitability for endangered species, aiding in the selection of optimal restoration sites [2].\n\n### 2. Wetland Monitoring and Mapping\n\nAI, combined with satellite data, facilitates precise mapping of wetland areas and the identification of illegal land conversions [3]. This precise monitoring allows for more effective management and conservation strategies.  Furthermore, new AI tools provide rapid biodiversity assessments, crucial for tracking the success of restoration projects [9].\n\n### 3. Climate Change Adaptation\n\nAI systems are utilized to predict the impacts of climate change on wetland ecosystems, enabling proactive management decisions [5].  This predictive capability is crucial for mitigating the effects of climate change on these sensitive environments.\n\n### 4. Biodiversity Enhancement\n\nAI has been instrumental in the development of dynamic models for improving biodiversity in degraded wetlands [6].  These models likely simulate various ecological interactions and predict responses to restoration interventions.  The integration of AI in coastal wetland restoration has also improved the success rate of re-establishing native vegetation [7].\n\n### 5. Policy and Decision Support\n\nAI is being used to simulate the socio-economic impacts of wetland restoration projects, aiding policymakers in decision-making [15].  Additionally, AI-driven decision-support tools are increasingly used for conservation prioritization [13].  Combining AI insights with indigenous knowledge enhances the cultural and ecological relevance of restoration projects [12].\n\n\n### Challenges and Future Directions\n\nWhile the applications of AI in wetland management are promising, challenges remain.  These include data scarcity and the transferability of models across different wetland types [10].  Further research and development are needed to address these limitations and fully realize the potential of AI in wetland conservation.\n\n## References\n\n [1] Johnson, A., & Ramirez, L. (2022). Artificial Intelligence in Environmental Conservation: A Comprehensive Review. Journal of Ecological Technology, 35(4), 125-140.\n\n[2] Smith, T., & Lee, C. (2021). Biodiversity and AI: Emerging Synergies. Biodiversity Insights, 18(2), 89-103.\n\n[3] Gupta, R., & Zhang, H. (2023). AI and Remote Sensing in Wetland Monitoring. Environmental Informatics, 29(3), 201-215.\n\n[5] Davis, M., & Nguyen, T. (2023). Role of AI in Climate Adaptation Strategies for Wetlands. Climatic Solutions, 12(4), 233-247.\n\n[6] Parker, E., & Singh, V. (2021). Enhancing Wetland Biodiversity Through Technology. Ecological Engineering, 46(2), 176-190.\n\n[7] Wilson, P., & Adebayo, F. (2022). AI-Driven Restoration Practices for Coastal Wetlands. Marine Ecology Advances, 14(3), 301-315.\n\n[9] Andersson, R., & Kim, S. (2023). AI-Powered Biodiversity Assessment Tools. Journal of Environmental Data Science, 15(1), 67-83.\n\n[10] Patel, A., & Roberts, M. (2021). Challenges and Opportunities in AI for Wetlands. Ecological Insights, 19(2), 150-170.\n\n[12] Rivera, J., & Khan, R. (2022). Integrating AI with Local Knowledge for Wetland Restoration. Global Conservation Journal, 9(4), 101-120.\n\n[13] Brooks, S., & Ahmed, L. (2021). AI-Enhanced Decision-Making in Wetland Conservation. Decision Science Quarterly, 14(1), 123-140.\n\n[15] Cooper, B., & Wang, L. (2022). AI in Wetland Restoration Policy Planning. Policy and Ecology, 11(3), 98-117.\n\n";
  const citations = {
    "1": {
    "title": "Artificial Intelligence in Environmental Conservation: A Comprehensive Review",
    "authors": "Johnson, A., & Ramirez, L.",
    "year": "2022",
    "journal": "Journal of Ecological Technology",
    "url": "https://www.jstor.org/stable/10.2307/j.eco.tech.35.4.125",
    "paper_id": "3784ccc8ab6bd0a08d808a7385191592a33a6fb3",
    "APA": "Johnson, A., & Ramirez, L. (2022). Artificial Intelligence in Environmental Conservation: A Comprehensive Review. Journal of Ecological Technology, 35(4), 125-140."
  },
  "2": {
    "title": "Biodiversity and AI: Emerging Synergies",
    "authors": "Smith, T., & Lee, C.",
    "year": "2021",
    "journal": "Biodiversity Insights",
    "url": "https://www.jstor.org/stable/10.2307/j.bio.insights.18.2.89",
    "paper_id": "2B",
    "APA": "Smith, T., & Lee, C. (2021). Biodiversity and AI: Emerging Synergies. Biodiversity Insights, 18(2), 89-103."
  },
  "3": {
    "title": "AI and Remote Sensing in Wetland Monitoring",
    "authors": "Gupta, R., & Zhang, H.",
    "year": "2023",
    "journal": "Environmental Informatics",
    "url": "https://www.jstor.org/stable/10.2307/j.env.informatics.29.3.201",
    "paper_id": "3C",
    "APA": "Gupta, R., & Zhang, H. (2023). AI and Remote Sensing in Wetland Monitoring. Environmental Informatics, 29(3), 201-215."
  },
  "4": {
    "title": "AI in Healthcare Diagnostics: A Transformative Approach",
    "authors": "Carter, J., & Lin, Y.",
    "year": "2020",
    "journal": "Journal of Medical AI",
    "url": "https://www.jstor.org/stable/10.2307/j.med.ai.12.3.44",
    "paper_id": "4D",
    "APA": "Carter, J., & Lin, Y. (2020). AI in Healthcare Diagnostics: A Transformative Approach. Journal of Medical AI, 12(3), 44-57."
  },
  "5": {
    "title": "Role of AI in Climate Adaptation Strategies for Wetlands",
    "authors": "Davis, M., & Nguyen, T.",
    "year": "2023",
    "journal": "Climatic Solutions",
    "url": "https://www.jstor.org/stable/10.2307/j.clim.sol.12.4.233",
    "paper_id": "5E",
    "APA": "Davis, M., & Nguyen, T. (2023). Role of AI in Climate Adaptation Strategies for Wetlands. Climatic Solutions, 12(4), 233-247."
  },
  "6": {
    "title": "Enhancing Wetland Biodiversity Through Technology",
    "authors": "Parker, E., & Singh, V.",
    "year": "2021",
    "journal": "Ecological Engineering",
    "url": "https://www.jstor.org/stable/10.2307/j.eco.eng.46.2.176",
    "paper_id": "6F",
    "APA": "Parker, E., & Singh, V. (2021). Enhancing Wetland Biodiversity Through Technology. Ecological Engineering, 46(2), 176-190."
  },
  "7": {
    "title": "AI-Driven Restoration Practices for Coastal Wetlands",
    "authors": "Wilson, P., & Adebayo, F.",
    "year": "2022",
    "journal": "Marine Ecology Advances",
    "url": "https://www.jstor.org/stable/10.2307/j.mar.eco.adv.14.3.301",
    "paper_id": "7G",
    "APA": "Wilson, P., & Adebayo, F. (2022). AI-Driven Restoration Practices for Coastal Wetlands. Marine Ecology Advances, 14(3), 301-315."
  },
  "8": {
    "title": "The Role of AI in Retail Demand Forecasting",
    "authors": "Miller, L., & Zhao, K.",
    "year": "2020",
    "journal": "Retail Innovation Quarterly",
    "url": "https://www.jstor.org/stable/10.2307/j.ret.inv.22.5.89",
    "paper_id": "8H",
    "APA": "Miller, L., & Zhao, K. (2020). The Role of AI in Retail Demand Forecasting. Retail Innovation Quarterly, 22(5), 89-112."
  },
  "9": {
    "title": "AI-Powered Biodiversity Assessment Tools",
    "authors": "Andersson, R., & Kim, S.",
    "year": "2023",
    "journal": "Journal of Environmental Data Science",
    "url": "https://www.jstor.org/stable/10.2307/j.env.data.sci.15.1.67",
    "paper_id": "9I",
    "APA": "Andersson, R., & Kim, S. (2023). AI-Powered Biodiversity Assessment Tools. Journal of Environmental Data Science, 15(1), 67-83."
  },
  "10": {
    "title": "Challenges and Opportunities in AI for Wetlands",
    "authors": "Patel, A., & Roberts, M.",
    "year": "2021",
    "journal": "Ecological Insights",
    "url": "https://www.jstor.org/stable/10.2307/j.eco.insights.19.2.150",
    "paper_id": "10J",
    "APA": "Patel, A., & Roberts, M. (2021). Challenges and Opportunities in AI for Wetlands. Ecological Insights, 19(2), 150-170."
  },
  "11": {
    "title": "AI in Sports Analytics: Revolutionizing Game Strategies",
    "authors": "Thomas, D., & Chen, J.",
    "year": "2023",
    "journal": "Journal of Sports Technology",
    "url": "https://www.jstor.org/stable/10.2307/j.sports.tech.30.2.180",
    "paper_id": "11K",
    "APA": "Thomas, D., & Chen, J. (2023). AI in Sports Analytics: Revolutionizing Game Strategies. Journal of Sports Technology, 30(2), 180-199."
  },
  "12": {
    "title": "Integrating AI with Local Knowledge for Wetland Restoration",
    "authors": "Rivera, J., & Khan, R.",
    "year": "2022",
    "journal": "Global Conservation Journal",
    "url": "https://www.jstor.org/stable/10.2307/j.glob.cons.9.4.101",
    "paper_id": "12L",
    "APA": "Rivera, J., & Khan, R. (2022). Integrating AI with Local Knowledge for Wetland Restoration. Global Conservation Journal, 9(4), 101-120."
  },
  "13": {
    "title": "AI-Enhanced Decision-Making in Wetland Conservation",
    "authors": "Brooks, S., & Ahmed, L.",
    "year": "2021",
    "journal": "Decision Science Quarterly",
    "url": "https://www.jstor.org/stable/10.2307/j.dec.sci.quar.14.1.123",
    "paper_id": "13M",
    "APA": "Brooks, S., & Ahmed, L. (2021). AI-Enhanced Decision-Making in Wetland Conservation. Decision Science Quarterly, 14(1), 123-140."
  },
  "14": {
    "title": "AI in Autonomous Vehicle Navigation",
    "authors": "Taylor, K., & Martinez, P.",
    "year": "2023",
    "journal": "Automotive AI Journal",
    "url": "https://www.jstor.org/stable/10.2307/j.auto.ai.25.3.245",
    "paper_id": "14N",
    "APA": "Taylor, K., & Martinez, P. (2023). AI in Autonomous Vehicle Navigation. Automotive AI Journal, 25(3), 245-260."
  },
  "15": {
    "title": "AI in Wetland Restoration Policy Planning",
    "authors": "Cooper, B., & Wang, L.",
    "year": "2022",
    "journal": "Policy and Ecology",
    "url": "https://www.jstor.org/stable/10.2307/j.pol.eco.11.3.98",
    "paper_id": "15O",
    "APA": "Cooper, B., & Wang, L. (2022). AI in Wetland Restoration Policy Planning. Policy and Ecology, 11(3), 98-117."
  }
};

  return (
    <div className="p-8 bg-white min-h-screen flex items-center justify-center">
      <div className="bg-white p-6 w-full max-w-3xl relative">
        <ResponseRenderer response={response} citations={citations}/>
        <button 
          className="mt-4 bg-emerald-500 text-white px-4 py-2 rounded-md hover:bg-emerald-600 transition-colors duration-200"
        >
          <span>Share</span>
        </button>
      </div>
    </div>
  );
};

export default ResponseRenderer;